import React, { useMemo } from 'react';
import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';

// 이름에서 () 괄호와 그 안의 내용 제거
const cleanName = (name) => {
  return name?.replace(/\s*\([^)]*\)\s*/g, '').trim();
};

// domain + subtype 복합키 생성 - dataTypeCounts 조회 시 항상 이 함수를 사용해야
// 서로 다른 domain에 같은 이름의 subtype("기타" 등)이 있어도 값이 섞이지 않는다.
// 백엔드(project-stats)의 compositeKey 생성 규칙과 반드시 동일해야 한다.
const makeDataTypeKey = (domainName, subTypeName) => {
  return `${cleanName(domainName) || '미입력'}::${cleanName(subTypeName) || '미입력'}`;
};

// 좌측 고정(sticky) 컬럼 3개의 폭과 left 오프셋
// 과제명(0) -> 게시물 수(180, 과제명 폭만큼 밀림) -> 최근 업로드(270, 과제명+게시물수 폭만큼 밀림)
const STICKY_COL = {
  projectName: { width: 180, left: 0 },
  postCount: { width: 90, left: 180 },
  latestUpload: { width: 110, left: 270 },
};
const STICKY_BORDER = '2px solid #bdbdbd'; // 고정 영역과 스크롤 영역을 구분하는 진한 경계선
const CELL_BORDER = '1px solid #e0e0e0'; // 일반 컬럼 간 세로 구분선
const GROUP_BORDER = '2px solid #90a4ae'; // 카테고리(데이터 대상 / 도메인·데이터 타입 / 접근 권한) 그룹 경계선

/**
 * props
 * - projects: 과제별 통계 배열
 * - allDomainNames: 전체 도메인 이름 목록 (DB, gubun=10)
 * - allDataTargetNames: 전체 데이터 대상 이름 목록 (DB, gubun=20)
 * - allAccessLevelNames: 전체 접근 권한 이름 목록 (DB, gubun=40)
 * - domainStructure: [{ code, name, subtypes: [{ code, name }, ...] }, ...]
 *
 * 레이아웃:
 * - 과제명 / 게시물 수 / 최근 업로드 3개 컬럼은 position: sticky로 좌측 고정하여
 *   테이블을 가로 스크롤해도 항상 보이도록 한다.
 * - 컬럼 간에는 세로 border를 넣어 값이 어느 컬럼에 속하는지 명확히 구분한다.
 * - "데이터 대상"과 "접근 권한(Access Level)"은 하위 분류가 1단뿐이므로 rowSpan=2로
 *   Row1~2를 병합해 2단으로 표시하고, "도메인 / 데이터 타입"은 대분류(도메인)+소분류(subtype)
 *   2단이 실제로 있어 3단(제목+도메인+subtype) 구조를 유지한다.
 * - 컬럼 순서: 고정 3열 -> 데이터 대상 -> 도메인/데이터 타입 -> 접근 권한(Access Level)
 *
 * 카테고리 그룹 경계선(GROUP_BORDER) 담당 원칙 - 헤더/바디/합계 행 전부 동일하게 적용:
 *   1) 고정 3열 ↔ 데이터 대상            : "데이터 대상"의 borderLeft 가 담당
 *   2) 데이터 대상 ↔ 도메인/데이터 타입   : "데이터 대상"의 borderRight 가 담당
 *   3) 도메인/데이터 타입 ↔ 접근 권한     : "접근 권한" 쪽 첫 컬럼의 borderLeft 가 담당
 *   (경계 하나당 반드시 한쪽 셀에서만 그려서, 두 셀이 각자 선을 그려 겹치거나
 *    반대로 서로 미루다 아예 안 그려지는 문제를 방지한다. rowSpan으로 세로로 긴 셀이
 *    있으면 그 셀 쪽에서 경계를 담당해야 전체 높이에 선이 끊김없이 이어진다.)
 */
const ResearchProjectTableComponent = ({
  projects,
  allDomainNames,
  allDataTargetNames,
  allAccessLevelNames,
  domainStructure,
}) => {
  const availableDomains = useMemo(() => {
    if (!Array.isArray(domainStructure) || domainStructure.length === 0) return [];
    return domainStructure.map((domain) => {
      const subtypeNames = (domain.subtypes || []).map((s) => cleanName(s.name));
      const hasEmpty = subtypeNames.includes('미입력');
      return {
        ...domain,
        subtypes: hasEmpty ? subtypeNames : [...subtypeNames, '미입력'],
      };
    });
  }, [domainStructure]);

  const dataTargetCategories = useMemo(() => {
    if (!allDataTargetNames) return [];
    const cleanedNames = allDataTargetNames.filter((name) => name && !name.includes('Data Target')).map(cleanName);
    const uniqueNames = [...new Set(cleanedNames)];
    if (!uniqueNames.includes('미입력')) {
      uniqueNames.push('미입력');
    }
    return uniqueNames;
  }, [allDataTargetNames]);

  // 접근 권한(Access Level) 카테고리 - () 안의 내용 제거 후 중복 제거, 미입력은 항상 마지막에 추가
  const accessLevelCategories = useMemo(() => {
    if (!allAccessLevelNames) return [];
    const cleanedNames = allAccessLevelNames.filter((name) => name && !name.includes('Access Level')).map(cleanName);
    const uniqueNames = [...new Set(cleanedNames)];
    if (!uniqueNames.includes('미입력')) {
      uniqueNames.push('미입력');
    }
    return uniqueNames;
  }, [allAccessLevelNames]);

  const getDataTargetCount = (project, target) => {
    if (!project.dataTargetCounts) return 0;
    const normalizedTarget = cleanName(target) || '미입력';
    return project.dataTargetCounts[normalizedTarget] || 0;
  };

  const getDataTargetTotal = (target) => {
    if (!Array.isArray(projects)) return 0;
    return projects.reduce((sum, project) => sum + getDataTargetCount(project, target), 0);
  };

  // 접근 권한 카운트 조회 - dataTargetCounts와 동일한 단순 키 조회 패턴 (domain 개념이 없어 안전)
  const getAccessLevelCount = (project, accessLevel) => {
    if (!project.accessLevelCounts) return 0;
    const normalizedLevel = cleanName(accessLevel) || '미입력';
    return project.accessLevelCounts[normalizedLevel] || 0;
  };

  const getAccessLevelTotal = (accessLevel) => {
    if (!Array.isArray(projects)) return 0;
    return projects.reduce((sum, project) => sum + getAccessLevelCount(project, accessLevel), 0);
  };

  const getDataTypeCount = (project, domain, subType) => {
    if (!project.dataTypeCounts) return 0;
    const compositeKey = makeDataTypeKey(domain.name, subType);
    return project.dataTypeCounts[compositeKey] || 0;
  };

  const getDataTypeGrandTotal = (domain, subType) => {
    if (!Array.isArray(projects)) return 0;
    return projects.reduce((sum, project) => sum + getDataTypeCount(project, domain, subType), 0);
  };

  const getTotalPostCount = () => {
    if (!Array.isArray(projects)) return 0;
    return projects.reduce((sum, p) => sum + (p.postCount || 0), 0);
  };

  const getDomainColSpan = (domain) => domain.subtypes.length;

  const totalDomainTypeColumns = useMemo(() => {
    return availableDomains.reduce((count, domain) => count + domain.subtypes.length, 0);
  }, [availableDomains]);

  const safeDomainTypeColSpan = totalDomainTypeColumns > 0 ? totalDomainTypeColumns : 1;
  const safeDataTargetColSpan = dataTargetCategories.length > 0 ? dataTargetCategories.length : 1;
  const safeAccessLevelColSpan = accessLevelCategories.length > 0 ? accessLevelCategories.length : 1;

  // 헤더용 sticky 스타일 - z-index를 바디보다 높게 주어 스크롤 시 겹침 순서가 항상 위에 오도록 함
  const stickyHeaderSx = (col, extra = {}) => ({
    position: 'sticky',
    left: STICKY_COL[col].left,
    minWidth: STICKY_COL[col].width,
    zIndex: 3,
    backgroundColor: '#f5f5f5',
    ...extra,
  });

  // 바디용 sticky 스타일 - 짝수/홀수 행 배경색과 맞춰야 겹침 없이 자연스럽다
  const stickyBodySx = (col, isOdd, extra = {}) => ({
    position: 'sticky',
    left: STICKY_COL[col].left,
    minWidth: STICKY_COL[col].width,
    zIndex: 2,
    backgroundColor: isOdd ? '#f9f9f9' : '#fff',
    ...extra,
  });

  // 도메인 subtype 셀의 borderRight - 도메인/데이터 타입 그룹 경계는 이제 "접근 권한"의 borderLeft가
  // 전담하므로, 여기서는 마지막 도메인의 마지막 subtype에서도 항상 'none'을 반환한다.
  const getDomainSubtypeBorderRight = (domain, domainIndex, typeIndex) => {
    if (typeIndex < domain.subtypes.length - 1) return CELL_BORDER;
    if (domainIndex < availableDomains.length - 1) return GROUP_BORDER;
    return 'none';
  };

  return (
    <Table sx={{ minWidth: 1400, borderCollapse: 'separate' }}>
      <TableHead>
        {/* Row 1 */}
        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
          <TableCell
            rowSpan={3}
            sx={stickyHeaderSx('projectName', {
              fontWeight: 'bold',
              textAlign: 'left',
              pl: 2,
              borderRight: CELL_BORDER,
            })}
          >
            과제명
          </TableCell>
          <TableCell
            rowSpan={3}
            sx={stickyHeaderSx('postCount', {
              fontWeight: 'bold',
              textAlign: 'center',
              borderRight: CELL_BORDER,
            })}
          >
            데이터 수
          </TableCell>
          <TableCell
            rowSpan={3}
            sx={stickyHeaderSx('latestUpload', {
              fontWeight: 'bold',
              textAlign: 'center',
              borderRight: STICKY_BORDER,
            })}
          >
            최근 업로드
          </TableCell>
          {/* 데이터 대상: rowSpan=2 로 Row1~2 전체 높이를 커버하므로, 좌우 경계 모두 여기서 담당 */}
          <TableCell
            rowSpan={2}
            colSpan={safeDataTargetColSpan}
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              verticalAlign: 'middle',
              backgroundColor: '#e3f2fd',
              fontSize: '14px',
              borderRight: GROUP_BORDER,
            }}
          >
            데이터 대상
          </TableCell>
          {/* 도메인 / 데이터 타입: rowSpan 없이 Row1 한 줄만 차지 -> 좌우 경계는 이웃(데이터 대상/접근 권한)이 담당하므로 여기선 그리지 않음 */}
          <TableCell
            colSpan={safeDomainTypeColSpan}
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              backgroundColor: '#fff3e0',
              fontSize: '14px',
              py: 1.2,
            }}
          >
            도메인 / 데이터 타입
          </TableCell>
          {/* 접근 권한: rowSpan=2 로 Row1~2 전체 높이를 커버하므로, 왼쪽 경계(도메인/데이터 타입과의 구분)를 여기서 담당 */}
          <TableCell
            rowSpan={2}
            colSpan={safeAccessLevelColSpan}
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              verticalAlign: 'middle',
              backgroundColor: '#e8f5e9',
              fontSize: '14px',
              borderLeft: GROUP_BORDER,
            }}
          >
            접근 권한
          </TableCell>
        </TableRow>

        {/* Row 2 - 데이터 대상/접근 권한 칸은 Row1의 rowSpan=2로 이미 채워졌으므로 도메인 대분류만 렌더링.
            좌우 경계는 "데이터 대상"/"접근 권한"이 이미 담당하고 있으므로 여기서는 그리지 않는다. */}
        <TableRow sx={{ backgroundColor: '#eceff1' }}>
          {availableDomains.map((domain, index) => (
            <TableCell
              key={`domain-header-${domain.code ?? index}`}
              colSpan={getDomainColSpan(domain)}
              sx={{
                fontWeight: 500,
                textAlign: 'center',
                fontSize: '13px',
                backgroundColor: index === 0 ? '#fff9c4' : index === 1 ? '#e1f5fe' : '#f3e5f5',
                borderRight: index < availableDomains.length - 1 ? CELL_BORDER : 'none',
              }}
            >
              {cleanName(domain.name)}
            </TableCell>
          ))}
        </TableRow>

        {/* Row 3 - 데이터 대상 소분류 헤더 + 도메인 하위 subtype 헤더 + 접근 권한 소분류 헤더 */}
        <TableRow sx={{ backgroundColor: '#e0f7fa' }}>
          {dataTargetCategories.map((target, index) => (
            <TableCell
              key={`dataTarget-sub-${index}`}
              sx={{
                fontWeight: 500,
                textAlign: 'center',
                fontSize: '13px',
                borderRight: index < dataTargetCategories.length - 1 ? CELL_BORDER : GROUP_BORDER,
              }}
            >
              {target}
            </TableCell>
          ))}
          {availableDomains.map((domain, domainIndex) => {
            const domainBgColor = domainIndex === 0 ? '#fff9c4' : domainIndex === 1 ? '#e1f5fe' : '#f3e5f5';
            return (
              <React.Fragment key={`domain-items-${domain.code ?? domainIndex}`}>
                {domain.subtypes.map((type, typeIndex) => (
                  <TableCell
                    key={`type-sub-${domainIndex}-${typeIndex}`}
                    sx={{
                      fontWeight: 400,
                      textAlign: 'center',
                      fontSize: '12px',
                      backgroundColor: domainBgColor,
                      borderRight: getDomainSubtypeBorderRight(domain, domainIndex, typeIndex),
                    }}
                  >
                    {type}
                  </TableCell>
                ))}
              </React.Fragment>
            );
          })}
          {accessLevelCategories.map((level, index) => (
            <TableCell
              key={`accessLevel-sub-${index}`}
              sx={{
                fontWeight: 500,
                textAlign: 'center',
                fontSize: '13px',
                backgroundColor: '#e8f5e9',
                borderLeft: index === 0 ? GROUP_BORDER : 'none',
                borderRight: index < accessLevelCategories.length - 1 ? CELL_BORDER : 'none',
              }}
            >
              {level}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {projects.map((project, index) => {
          const isOdd = index % 2 === 1;
          return (
            <TableRow key={project.projectName || index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
              <TableCell
                sx={stickyBodySx('projectName', isOdd, {
                  textAlign: 'left',
                  pl: 2,
                  borderRight: CELL_BORDER,
                })}
              >
                {project.projectName || '-'}
              </TableCell>
              <TableCell
                sx={stickyBodySx('postCount', isOdd, {
                  textAlign: 'center',
                  borderRight: CELL_BORDER,
                })}
              >
                {project.postCount || 0}
              </TableCell>
              <TableCell
                sx={stickyBodySx('latestUpload', isOdd, {
                  textAlign: 'center',
                  borderRight: STICKY_BORDER,
                })}
              >
                {project.latestUpload || '-'}
              </TableCell>

              {dataTargetCategories.map((target, targetIndex) => (
                <TableCell
                  key={`dataTarget-count-${targetIndex}`}
                  sx={{
                    textAlign: 'center',
                    fontSize: '12px',
                    borderRight: targetIndex < dataTargetCategories.length - 1 ? CELL_BORDER : GROUP_BORDER,
                  }}
                >
                  {getDataTargetCount(project, target)}
                </TableCell>
              ))}

              {availableDomains.map((domain, domainIndex) => (
                <React.Fragment key={`domain-data-${domain.code ?? domainIndex}`}>
                  {domain.subtypes.map((type, typeIndex) => (
                    <TableCell
                      key={`count-${domainIndex}-${typeIndex}`}
                      sx={{
                        textAlign: 'center',
                        fontSize: '12px',
                        borderRight: getDomainSubtypeBorderRight(domain, domainIndex, typeIndex),
                      }}
                    >
                      {getDataTypeCount(project, domain, type)}
                    </TableCell>
                  ))}
                </React.Fragment>
              ))}

              {accessLevelCategories.map((level, levelIndex) => (
                <TableCell
                  key={`accessLevel-count-${levelIndex}`}
                  sx={{
                    textAlign: 'center',
                    fontSize: '12px',
                    borderLeft: levelIndex === 0 ? GROUP_BORDER : 'none',
                    borderRight: levelIndex < accessLevelCategories.length - 1 ? CELL_BORDER : 'none',
                  }}
                >
                  {getAccessLevelCount(project, level)}
                </TableCell>
              ))}
            </TableRow>
          );
        })}

        {/* 전체 합계 행 */}
        {projects.length > 0 && (
          <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
            <TableCell
              sx={{
                position: 'sticky',
                left: STICKY_COL.projectName.left,
                minWidth: STICKY_COL.projectName.width,
                zIndex: 2,
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold',
                borderRight: CELL_BORDER,
              }}
            >
              합계
            </TableCell>
            <TableCell
              sx={{
                position: 'sticky',
                left: STICKY_COL.postCount.left,
                minWidth: STICKY_COL.postCount.width,
                zIndex: 2,
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: CELL_BORDER,
              }}
            >
              {getTotalPostCount()}
            </TableCell>
            <TableCell
              sx={{
                position: 'sticky',
                left: STICKY_COL.latestUpload.left,
                minWidth: STICKY_COL.latestUpload.width,
                zIndex: 2,
                backgroundColor: '#e3f2fd',
                fontWeight: 'bold',
                textAlign: 'center',
                borderRight: STICKY_BORDER,
              }}
            >
              -
            </TableCell>

            {dataTargetCategories.map((target, targetIndex) => (
              <TableCell
                key={`total-target-${targetIndex}`}
                sx={{
                  textAlign: 'center',
                  fontSize: '12px',
                  borderRight: targetIndex < dataTargetCategories.length - 1 ? CELL_BORDER : GROUP_BORDER,
                }}
              >
                {getDataTargetTotal(target)}
              </TableCell>
            ))}

            {availableDomains.map((domain, domainIndex) => (
              <React.Fragment key={`total-${domain.code ?? domainIndex}`}>
                {domain.subtypes.map((type, typeIndex) => (
                  <TableCell
                    key={`total-type-${domainIndex}-${typeIndex}`}
                    sx={{
                      textAlign: 'center',
                      fontSize: '12px',
                      borderRight: getDomainSubtypeBorderRight(domain, domainIndex, typeIndex),
                    }}
                  >
                    {getDataTypeGrandTotal(domain, type)}
                  </TableCell>
                ))}
              </React.Fragment>
            ))}

            {accessLevelCategories.map((level, levelIndex) => (
              <TableCell
                key={`total-accessLevel-${levelIndex}`}
                sx={{
                  textAlign: 'center',
                  fontSize: '12px',
                  borderLeft: levelIndex === 0 ? GROUP_BORDER : 'none',
                  borderRight: levelIndex < accessLevelCategories.length - 1 ? CELL_BORDER : 'none',
                }}
              >
                {getAccessLevelTotal(level)}
              </TableCell>
            ))}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default ResearchProjectTableComponent;
